import { Button, Form, Input, Modal } from "antd";
import { useEffect, useState } from "react";

interface Props {
  open: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  /** Modal heading — defaults to the Resend wording. */
  title?: string;
  label?: string;
  confirmText?: string;
  /** Renders the confirm button red, for Reject / Cancel. */
  danger?: boolean;
  loading?: boolean;
}

const REASON_LIMIT = 5000;

/**
 * Single-textarea "give a reason" modal.
 *
 * Three workflow actions need exactly this — Resend, Reject
 * (`PUT /workflow/reject`) and Cancel (`PUT /workflow/cancel`) all take one
 * mandatory free-text comment — so the copy is props rather than three
 * near-identical modals. The confirm button stays disabled until something is
 * typed, since the endpoints treat the comment as required.
 */
export const ReasonModal = ({
  open,
  onCancel,
  onConfirm,
  title = "Resend",
  label = "Reason for Resend",
  confirmText = "Resend with comment",
  danger,
  loading,
}: Props) => {
  const [reason, setReason] = useState("");
  /* Empty every time it closes — after a submit too, not only on Cancel; the
     last reason was still there when the modal was opened again. A failed
     submit leaves it open, so what was typed is kept for a retry. */
  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  return (
    <Modal
      open={open}
      title={title}
      centered
      maskClosable={false}
      width={720}
      onCancel={onCancel}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            danger={danger}
            loading={loading}
            disabled={!reason.trim()}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirmText}
          </Button>
        </div>
      }
    >
      <Form layout="vertical">
        <Form.Item required label={label}>
          <Input.TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={8}
            showCount
            maxLength={REASON_LIMIT}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
